import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthContext } from '@/hooks/useAuthContext';
import { Button, Input, Card, CardHeader, CardTitle, CardContent } from '@/components/ui';
import { ErrorMessage } from '@/components/common';

import { env } from '@/lib/env';

// Determine auth mode from environment
const AUTH_MODE = env.AUTH_MODE;

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const auth = useAuthContext();
  
  const navigate = useNavigate();
  const location = useLocation();

  // Get the redirect path from location state, or default to dashboard
  const from = (location.state as { from?: string })?.from || '/';

  // Redirect if already authenticated
  React.useEffect(() => {
    if (auth.isAuthenticated && !auth.isLoading) {
      // Don't redirect to /login, always go to dashboard
      const redirectTo = from === '/login' ? '/' : from;
      
      // Only redirect if we're currently on the login page
      if (location.pathname === '/login') {
        navigate(redirectTo, { replace: true });
      }
    }
  }, [auth.isAuthenticated, auth.isLoading, from, navigate, location.pathname]);

  // Auto-redirect to Entra SSO if auth mode is entra and not yet authenticated
  React.useEffect(() => {
    if (AUTH_MODE === 'entra' && !auth.isLoading && !auth.isAuthenticated) {
      (auth.login as () => Promise<void>)();
    }
  }, [auth.isLoading, auth.isAuthenticated]);

  // Show spinner while MSAL is initializing or auto-redirecting to Entra
  if (auth.isLoading || (AUTH_MODE === 'entra' && !auth.isAuthenticated)) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-neutral-600">Signing in...</p>
        </div>
      </div>
    );
  }

  // Handle mock auth login
  const handleMockSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      // Mock auth login takes email and password
      await (auth as { login: (email: string, password: string) => Promise<void> }).login(email, password);
      
      // Redirect to the page they tried to access, or dashboard
      navigate(from, { replace: true });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Invalid email or password';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle Entra ID login
  const handleEntraLogin = async () => {
    setError('');
    setIsLoading(true);

    try {
      // Entra login doesn't take parameters - cast to any to handle union type
      await (auth as { login: () => Promise<void> }).login();
      // Redirect happens automatically after Microsoft login
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Login failed';
      setError(errorMessage);
      setIsLoading(false);
    }
  };

  // Render Entra ID login
  if (AUTH_MODE === 'entra') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50 py-12 px-4 sm:px-6 lg:px-8">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="text-center">Sign in to Holds Request</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {error && <ErrorMessage message={error} />}

              <Button 
                onClick={handleEntraLogin} 
                className="w-full bg-[#0078d4] hover:bg-[#106ebe] text-white"
                isLoading={isLoading}
              >
                <svg className="w-5 h-5 mr-2" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
                  <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
                  <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
                  <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
                </svg>
                Login with Microsoft
              </Button>

              <p className="text-sm text-center text-neutral-600 mt-4">
                Use your Microsoft account to sign in
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Render mock auth login
  return (
    <div className="min-h-screen flex items-center justify-center bg-neutral-50 py-12 px-4 sm:px-6 lg:px-8">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-center">Sign in to Holds Request</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleMockSubmit} className="space-y-4">
            {error && <ErrorMessage message={error} />}

            <Input
              label="Email address"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              disabled={isLoading}
            />

            <Input
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              disabled={isLoading}
            />

            <Button type="submit" className="w-full" isLoading={isLoading}>
              Sign in
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
